import { useCallback } from 'react'
import { Product } from 'modules/Cube/domain/cube.domain.types'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import {
  QUOTE_PRESENTATION_V3_DEFAULT,
  SEAT_BASED_MINIMUM_TEXT_ALTERNATIVE
} from 'Cube/domain/cube.constants'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

export type QuoteMutationInterface = v1ApiUpdateQuote & {
  products: {
    new: Product[]
  }
}

export type ApiClientUpdatedQuoteModel =
  DashboardApi20240730.Quotes.QuoteUpdateResponse['data']

export type ApiClientCreatedQuoteModel = NonNullable<
  DashboardApi20240730.PostQuote.CreateResponse['data']
>

export type ApiClientQuoteModel = DashboardApi20240730.Quotes.Quote
export type ApiClientArchivedQuote =
  DashboardApi20240730.PostQuoteArchive.ArchiveResponse['data']

export type ApiClientPublishQuoteOutcome =
  DashboardApi20240730.PostQuotePublish.PublishQuoteOutcome

export type QuoteExecuteScheduleAction =
  DashboardApi20240730.PostQuoteExecute.QuoteExecuteScheduleAction

type UseSaveQuote = () => {
  create: (
    data?: Partial<ApiClientQuoteModel>
  ) => Promise<ApiClientCreatedQuoteModel | null>
  update: (
    id: string,
    data: v1ApiUpdateQuote
  ) => Promise<ApiClientQuoteModel | null>
  publishQuote: (
    id: string,
    data: PublishQuoteData
  ) => Promise<ApiClientPublishQuoteOutcome | null>
  accept: (id: string) => Promise<ApiClientUpdatedQuoteModel | null>
  execute: (
    id: string,
    scheduleAction: QuoteExecuteScheduleAction
  ) => Promise<ApiClientUpdatedQuoteModel | null>
  archive: (id: string) => Promise<ApiClientArchivedQuote | null>
  deleteLatestDraft: (id: string) => Promise<ApiClientQuoteModel | null>
  duplicate: (id: string) => Promise<ApiClientQuoteModel | null>
}

export type v1ApiUpdateQuote = DashboardApi20240730.PutQuote.PutQuoteBody

export type PublishQuoteData = {
  /** The emails to send the published quote to. */
  recipients: string[] | undefined
  sendRecipientsEmail: boolean
  emailMessage: string | undefined
  makeReadyToSign: boolean
  messageForApprover: string | undefined
}

export const useSaveQuote: UseSaveQuote = () => {
  const flags = useFeatureFlags()

  const create = useCallback(
    async (data: Partial<ApiClientQuoteModel> = {}) => {
      const postQuoteResponse = await dashboard20240730Client.postQuote({
        body: { ...data }
      })

      if (postQuoteResponse.error || !postQuoteResponse.data) {
        return null
      }

      /**
       * POST /quote doesn't currently allow `presentation` to be set in its request body, so we need to do a
       * PUT immediately after creating the quote to populate `presentation` with defaults.
       *
       * The POST /quote endpoint creates `presentation: []` by default. We previously just let
       * `apiPresentationToQuotePresentation` convert the empty presentation array to a proper presentation object
       * during the first PUT /quote/:id request while editing.
       *
       * However, we now want to have different default settings for presentation V3 depending on whether a quote
       * is freshly created, or an existing quote that is being migrated to the new version. For example,
       * `showPdfCoverPage` should default to `false` for a migrated quote, but should be `true` for a new quote.
       */
      const putQuoteResponse = await dashboard20240730Client.putQuote({
        id: postQuoteResponse.data.id,
        body: {
          ...postQuoteResponse.data,
          phases: [],
          contactIds: [],
          attachmentAssetIds: [],
          /**
           * When `useAlternativeQuoteSeatBasedMinimumText` is enabled for an account, show the text 'Contracted'
           * rather than the default 'Contracted minimum' when rendering seat-based prices and grouping products by phase
           *
           * Originally enabled for 11x, more details here:
           * https://linear.app/sequence-hq/issue/BADGE-4882/allow-contracted-minimum-copy-on-quotes-to-be-configurable
           */
          presentation: flags.useAlternativeQuoteSeatBasedMinimumText
            ? {
                ...QUOTE_PRESENTATION_V3_DEFAULT,
                settings: {
                  ...QUOTE_PRESENTATION_V3_DEFAULT.settings,
                  seatBasedMinimumText: SEAT_BASED_MINIMUM_TEXT_ALTERNATIVE
                }
              }
            : QUOTE_PRESENTATION_V3_DEFAULT
        }
      })

      if (putQuoteResponse.error || putQuoteResponse.data?.status !== 'DRAFT') {
        return null
      }

      return putQuoteResponse.data
    },
    [flags.useAlternativeQuoteSeatBasedMinimumText]
  )

  const update = useCallback(async (id: string, data: v1ApiUpdateQuote) => {
    const response = await dashboard20240730Client.putQuote({
      id,
      body: data
    })

    if (response.error) {
      // Parse the response body and throw a structured error
      const errorBody = await response.res.json().catch(() => ({}))
      throw {
        status: response.res.status,
        statusText: response.res.statusText,
        response: errorBody
      }
    }

    return response.data ?? null
  }, [])

  const publishQuote = useCallback(
    async (id: string, data: PublishQuoteData) => {
      const response = await dashboard20240730Client.postQuotePublish({
        id,
        body: {
          recipients: data.recipients,
          sendRecipientsEmail: data.sendRecipientsEmail,
          emailMessage: data.emailMessage,
          makeReadyToSign: data.makeReadyToSign,
          messageForApprover: data.messageForApprover
        }
      })

      if (response.error) {
        // Parse the response body and throw a structured error
        const errorBody = await response.res.json().catch(() => ({}))
        throw {
          status: response.res.status,
          statusText: response.res.statusText,
          response: errorBody
        }
      }

      return response.data ?? null
    },
    []
  )

  const accept = useCallback(async (id: string) => {
    const response = await dashboard20240730Client.postQuoteAccept({ id })

    if (response.error) {
      return null
    }

    return response.data ?? null
  }, [])

  const execute = useCallback(
    async (id: string, scheduleAction: QuoteExecuteScheduleAction) => {
      const response = await dashboard20240730Client.postQuoteExecute({
        id,
        body: { scheduleAction }
      })

      if (response.error) {
        return null
      }

      return response.data ?? null
    },
    []
  )

  const archive = useCallback(async (id: string) => {
    const response = await dashboard20240730Client.postQuoteArchive({ id })

    if (response.error) {
      return null
    }

    return response.data ?? null
  }, [])

  const deleteLatestDraft = useCallback(async (id: string) => {
    const response = await dashboard20240730Client.deleteQuoteDraftVariant({
      id
    })

    if (response.error) {
      return null
    }

    return response.data ?? null
  }, [])

  const duplicate = useCallback(async (id: string) => {
    const response = await dashboard20240730Client.postQuoteDuplicate({ id })

    if (response.error) {
      return null
    }

    return response.data ?? null
  }, [])

  return {
    create,
    update,
    publishQuote,
    accept,
    execute,
    archive,
    deleteLatestDraft,
    duplicate
  }
}
